import React from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useReveal from "../../../hooks/useReveal";

/**
 * LANDING WIDGETS
 * ===============
 * The landing-page counterpart to DashboardWidgets: the small set of shells the
 * marketing sections compose from, so vertical rhythm, container width and
 * heading hierarchy are defined once instead of per section.
 *
 * Two things this replaces:
 *   - `.container`, which is capped at 1090px with !important in
 *     styles/abstract/_utility.scss. Fine for a text column, too narrow for a
 *     modern landing, and impossible to override from a utility class.
 *   - per-section margin guesses (`mb-40`, `mb-48`, `mb-7xl`, `pt-32`), which
 *     had drifted far enough apart that section spacing read as accidental.
 *
 * Colors are semantic tokens, so the landing keeps the blue brand from :root
 * while the same components would re-theme correctly anywhere else.
 */

/** Centered content column. One width for the whole page. */
export function Shell({ className = "", children }) {
    return (
        <div className={`mx-auto w-full max-w-[124rem] px-xl max-sm:px-lg ${className}`}>
            {children}
        </div>
    );
}

/**
 * A page section: owns its vertical rhythm, its background tone, and its own
 * scroll-reveal scope (children marked `.reveal` animate in via landing.scss).
 *
 * tone: "default" | "brand" | "dark"
 */
export function Section({
    id,
    tone = "default",
    className = "",
    shellClassName = "",
    children,
}) {
    const [ref, inView] = useReveal();

    const tones = {
        default: "bg-transparent text-ink",
        brand: "text-white",
        dark: "bg-navyDeep text-white",
    };

    return (
        <section
            id={id}
            ref={ref}
            className={`${inView ? "in-view" : ""} ${tones[tone] ?? tones.default} py-6xl max-sm:py-5xl ${className}`}
        >
            <Shell className={shellClassName}>{children}</Shell>
        </section>
    );
}

/**
 * Section heading block. `accent` is the decorative SVG some sections sit
 * beside the title - it is wrapped so it wipes in with `.draw-accent`.
 */
export function SectionHeading({
    title,
    description,
    accent,
    align = "start",
    tone = "default",
    className = "",
}) {
    const alignment =
        align === "center"
            ? "items-center text-center mx-auto"
            : "items-start text-start";

    const titleTone = tone === "invert" ? "text-white" : "text-ink-strong";
    const bodyTone = tone === "invert" ? "text-white/80" : "text-ink-muted";

    return (
        <div className={`flex flex-col ${alignment} ${className}`}>
            <h2
                className={`reveal reveal--up relative text-4xl font-bold leading-tight max-sm:text-3xl ${titleTone}`}
                style={{ "--i": 0 }}
            >
                {title}
                {accent && (
                    <span className="pointer-events-none absolute -bottom-2 -right-14 hidden md:inline-block">
                        <span className="draw-accent">{accent}</span>
                    </span>
                )}
            </h2>
            {description && (
                <p
                    className={`reveal reveal--up mt-lg max-w-[68ch] text-lg leading-relaxed ${bodyTone}`}
                    style={{ "--i": 1 }}
                >
                    {description}
                </p>
            )}
        </div>
    );
}

/**
 * Surface card used across the landing. `tone` tints the top rail and the icon
 * well, which is how the four feature accents survive the redesign without the
 * old full-card colour swap.
 */
export function LandingCard({
    accent,
    className = "",
    interactive = true,
    children,
}) {
    return (
        <div
            className={`landing-lift relative flex h-full flex-col overflow-hidden rounded-panel border border-rule-subtle bg-surface p-2xl text-start shadow-card max-sm:p-xl ${
                interactive ? "hover:-translate-y-1 hover:shadow-headerCard" : ""
            } ${className}`}
        >
            {accent && (
                <span
                    aria-hidden="true"
                    className="absolute inset-x-0 top-0 h-[4px]"
                    style={{ backgroundColor: accent }}
                />
            )}
            {children}
        </div>
    );
}

/**
 * Shared field styling for the auth dialogs. Both forms previously rewrote this
 * string on every input, which is why the two had drifted (one had rounded
 * corners only on the first/last field, the other on none) and why the focus
 * state was missing entirely - `focus:outline-none` with nothing to replace it.
 */
export const AUTH_FIELD =
    "w-full rounded-soft border border-rule-interactive bg-surface px-lg py-md text-md font-light text-ink transition-colors duration-150 placeholder:text-ink-subtle focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 motion-reduce:transition-none";

/**
 * Dialog frame for login / pre-registration.
 *
 * Both dialogs were a bare white panel with a floating <Close> icon that was
 * not a button: not focusable, no accessible name, and positioned with
 * `right-12` which is the wrong side once the page is RTL. This gives them a
 * titled header, a real close control and a scroll-capped body.
 */
export function AuthPanel({
    title,
    description,
    close,
    width = "max-w-[48rem]",
    footer,
    children,
}) {
    return (
        <div className="flex h-full w-full items-center justify-center p-lg">
            <div
                className={`relative flex max-h-[92vh] w-full flex-col overflow-hidden rounded-panel bg-surface text-start shadow-headerCard ${width}`}
            >
                <div className="flex items-start justify-between gap-md border-b border-rule-subtle p-2xl">
                    <div className="min-w-0">
                        <h2 className="text-2xl font-bold text-ink-strong">{title}</h2>
                        {description && (
                            <p className="mt-xs text-base text-ink-muted">
                                {description}
                            </p>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        aria-label="بستن"
                        className="flex h-[3.6rem] w-[3.6rem] shrink-0 items-center justify-center rounded-soft text-ink-subtle transition-colors duration-150 hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary motion-reduce:transition-none"
                    >
                        <CloseRoundedIcon sx={{ fontSize: 24 }} />
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-2xl">{children}</div>

                {footer && (
                    <div className="border-t border-rule-subtle p-2xl">{footer}</div>
                )}
            </div>
        </div>
    );
}

/** Small circular icon well, tinted with the card's accent. */
export function IconWell({ src, alt = "", accent }) {
    return (
        <span
            className="mb-lg inline-flex h-[5.6rem] w-[5.6rem] shrink-0 items-center justify-center rounded-card"
            style={{ backgroundColor: `${accent}1F` }}
        >
            <img src={src} alt={alt} className="h-[2.8rem] w-[2.8rem] object-contain" />
        </span>
    );
}
